import React, {useState, useRef, useMemo, useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import useSWR from 'swr';
import {getApplicationPostProcessors} from '../../../api/postprocessor/GetApplicationPostProcessors';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {Project} from '../../../types/project/Project';
import {usePostProcessingScript} from '../../PrePostProcessing/PostProcessing/usePostProcessingScript';
import {ISPResult} from '../ISPResult';
import {NoDeployment} from '../NoDeployment';
import {TASK_STATUS, useDeploy} from '../useDeploy';
import {VideoStream} from '../VideoStream';
import './TestInferenceResults.scss';
import {TestResultParameters} from './TestResultParameters';
import {UnfinishedTestResults} from './UnfinishedTestResults';
import {StaticTestResults} from './StaticTestResults';
import {MaxSecondsMessage} from './MaxSecondsMessage';
import {WaitMessage} from './WaitMessage';

export type WebCameraTestInferenceResultsProps = {
  deployment: DeploymentDetailsInfo | null;
  hasTransformation: boolean;
  isStreamingInference: boolean;
  isOtherTaskInProgress: boolean;
  projectData: Project;
  spaId?: string;
  spaPostProcessorId?: string;
  displayRealtimeParameters: boolean;
};

export const WebCameraTestInferenceResults = ({
  deployment,
  hasTransformation,
  isStreamingInference,
  isOtherTaskInProgress,
  projectData,
  spaId,
  spaPostProcessorId,
  displayRealtimeParameters,
}: WebCameraTestInferenceResultsProps) => {
  const [search] = useState('');
  const videoDataContainerRef = useRef<HTMLDivElement>(null);
  const [{streamingTask, deployConfig}] = useDeploy();
  const isTaskInProgress = streamingTask.status === TASK_STATUS.IN_PROGRESS;
  const projectId = projectData.id;
  const applicationId = deployConfig.deploymentId;
  const isFetchingStreamingInference = useMemo(
    () => streamingTask.status === TASK_STATUS.IN_PROGRESS,
    [streamingTask?.status]
  );

  const {data: postProcessorsResponse} = useSWR(
    projectId && applicationId
      ? [projectId, applicationId, '/postprocessor/get/application']
      : null,
    () => getApplicationPostProcessors({projectId, applicationId, spaPostProcessorId}),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const postProcessors = postProcessorsResponse?.body || [];
  const browserPostProcessor = postProcessors.find(
    postProcessor =>
      postProcessor.executionContainer === 'BROWSER' &&
      postProcessor.postProcessorType === 'APPLICATION'
  );
  const filePaths = browserPostProcessor?.filePaths || [];
  const fileName = filePaths.find(path => path !== 'metadata.yaml');

  const module = usePostProcessingScript({
    projectId,
    applicationId,
    id: 'latest',
    hasPostProcessors:
      projectData.applications[applicationId]?.postProcessors !== undefined,
    fileName,
    postProcessorType: 'APPLICATION',
    executionContainer: 'BROWSER',
    viewOnly: true,
  });

  useEffect(() => {
    if (module) {
      try {
        if ('displayVideoData' in module) {
          if (
            streamingTask.data &&
            videoDataContainerRef.current &&
            module.displayVideoData
          ) {
            module.displayVideoData(videoDataContainerRef.current, streamingTask.data);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [module, streamingTask.data]);

  if (!isTaskInProgress && !isOtherTaskInProgress && !deployment) {
    return (
      <NoDeployment
        imageUrl="images/emptyStateNoResults.png"
        message={<FormattedMessage id="test.resultsWillShow" />}
      />
    );
  }

  if (isStreamingInference) {
    if (isFetchingStreamingInference) {
      return <WaitMessage />;
    }

    const {status, userStream, remoteStream, receiveOnly} = streamingTask;

    return (
      <section className="test-inference-results__video-container">
        <div className="test-inference-results__video">
          <div className="test-inference-results__video-main webcamera">
            <VideoStream
              receiveOnly={receiveOnly}
              status={status}
              userStream={userStream}
              remoteStream={remoteStream}
            />
            {/* Container that is populated with BROWSER post processor */}
            <div id="test-inference-results-video-data" ref={videoDataContainerRef} />
          </div>
          <MaxSecondsMessage />
          {applicationId && displayRealtimeParameters && (
            <TestResultParameters
              projectId={projectId}
              applicationId={applicationId}
              spaId={spaId}
              useStreamingTask={true}
            />
          )}
        </div>
      </section>
    );
  }

  if (isTaskInProgress || isOtherTaskInProgress) {
    return <UnfinishedTestResults />;
  }

  if (deployment) {
    // For some reason, backend sends a string instead of JSON.
    // So string comparison is used to verify if results are empty
    const videoInputs = new Set(['WEB_CAMERA', 'IP_CAMERA', 'FILE', 'APP_INPUT']);
    const sourceType = deployment.deploymentInput?.inputSourceType || '-';
    const hasResults = deployment.deploymentType !== 'ONNX_WEB_INFERENCE';

    if (
      hasResults &&
      deployment.inferenceResults &&
      deployment.inferenceResults !== '{}' &&
      !videoInputs.has(sourceType)
    ) {
      return (
        <StaticTestResults
          projectId={projectId}
          search={search}
          deployment={deployment}
          deployConfig={deployConfig}
          projectData={projectData}
        />
      );
    }

    if (hasResults && hasTransformation) {
      return <ISPResult projectId={projectId} deploymentId={deployment.id} />;
    }

    return (
      <NoDeployment
        imageUrl="images/emptyStateNoResults.png"
        message={<FormattedMessage id="test.videoStreamingHasFinished" />}
      />
    );
  }

  return null;
};
